// src/pages/reports/IrAnalysis/ReportIrFilter.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Checkbox,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
import { getToken } from "../../../utils/utils";
import { getReportsFilterOptions_Ir } from "../api";
import { getalllocations, getalloutlettype } from "../../assign-task/api";

/** 🔹 NEW: light client-side cleaners to avoid duplicates like Paid vs PAID */
const cleanStr = (s: any) => String(s ?? "").trim().replace(/\s+/g, " ");
const uniqueCI = (arr: any[]) => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of arr || []) {
    const k = cleanStr(v).toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(cleanStr(v)); // keep first-seen casing/spacing
  }
  return out;
};

/** Rewards-style small arrow that rotates and is clickable */
const CustomArrow = ({ open, onClick }: { open?: boolean; onClick?: () => void }) => (
  <span
    onClick={(e) => {
      e.stopPropagation();
      onClick?.();
    }}
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRight: "2px solid #222",
      borderBottom: "2px solid #222",
      transform: open ? "rotate(-135deg)" : "rotate(45deg)",
      transformOrigin: "center center",
      transition: "transform 0.18s ease",
      marginLeft: 12,
      position: "relative",
      cursor: "pointer",
      boxSizing: "border-box",
    }}
  />
);

const SELECT_ALL_LABEL = "Select All";

/** 🔎 Searchable multi-select (parity with ReportsLogFilter) */
function SearchableMultiSelect({
  label,
  options,
  value,
  onChange,
  loading,
  formatOptionLabel,
}: {
  label: string;
  options: string[];
  value: string[];
  onChange: (vals: string[]) => void;
  loading?: boolean;
  /** display-only formatter; raw values preserved for backend */
  formatOptionLabel?: (s: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const fmt = (s: string) => (formatOptionLabel ? formatOptionLabel(s) : s);

  // close on outside click / ESC
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearchValue("");
      }
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && (setOpen(false), setSearchValue(""));
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const filtered = (options || []).filter((opt) =>
    fmt(opt).toLowerCase().includes(searchValue.toLowerCase())
  );

  const allSelected = value.length > 0 && value.length === options.length;
  const displayedValue =
    value.length === 0 ? label : value.length <= 2 ? value.map(fmt).join(", ") : `${value.slice(0, 2).map(fmt).join(", ")}…`;

  const toggleOption = (opt: string) =>
    value.includes(opt) ? onChange(value.filter((v) => v !== opt)) : onChange([...value, opt]);

  const toggleSelectAll = () => (allSelected ? onChange([]) : onChange(options.slice()));

  return (
    <Box ref={containerRef} sx={{ width: "100%", position: "relative", userSelect: "none" }}>
      <TextField
        size="small"
        variant="outlined"
        value={displayedValue}
        onClick={() => setOpen((v) => !v)}
        placeholder={label}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <>
              {loading ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
              <InputAdornment position="end" sx={{ cursor: "pointer" }}>
                <CustomArrow open={open} onClick={() => setOpen((v) => !v)} />
              </InputAdornment>
            </>
          ),
          sx: {
            cursor: "pointer",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
        fullWidth
      />

      {open && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            maxHeight: 250,
            overflowY: "auto",
            border: "1px solid #ccc",
            backgroundColor: "#fff",
            zIndex: 3500,
            mt: 0.5,
            borderRadius: 1,
            boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ px: 1, py: 0.5 }}>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search..."
              style={{
                width: "100%",
                padding: "10px 10px",
                borderRadius: 4,
                border: "1px solid #ddd",
                fontSize: "0.9rem",
                marginBottom: 8,
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
                marginTop: "5px",
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </Box>

          <Box sx={{ px: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, cursor: "pointer" }} onClick={toggleSelectAll}>
              <Checkbox checked={allSelected} indeterminate={value.length > 0 && !allSelected} />
              <Typography variant="body2">{SELECT_ALL_LABEL}</Typography>
            </Box>

            {filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
                No options found
              </Typography>
            )}

            {filtered.map((opt) => {
              const checked = value.includes(opt);
              return (
                <Box
                  key={opt}
                  sx={{ display: "flex", alignItems: "center", py: 0.5, cursor: "pointer" }}
                  onClick={() => toggleOption(opt)}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Checkbox checked={checked} />
                  <Typography variant="body2">{fmt(opt)}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ==== Props (STRICT IR) ====
export interface ReportIrFilterProps {
  isOpen?: boolean;
  shouldFetch?: boolean; // when true + open, fetch initial options
  onFetched?: () => void; // notify parent initial fetch done

  initialFilters?: Partial<{
    // Strict backend keys
    locations: string[] | null;        // city list
    outlet_category: string[] | null;  // category list
    pmi_entity_code: string[] | null;  // PMI list
    outlet_name: string[] | null;      // outlet list
  }> | null;

  onApplyFilters: (filters: {
    locations: string[];
    outlet_category: string[];
    pmi_entity_code: string[];
    outlet_name: string[];
  }) => void;

  onCancel?: () => void;
}

export default function ReportIrFilter({
  isOpen = false,
  shouldFetch = false,
  onFetched,
  initialFilters = null,
  onApplyFilters,
  onCancel,
}: ReportIrFilterProps) {
  const token = getToken();

  // Lock body scroll (unchanged)
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevLeft = document.body.style.left;
    const prevRight = document.body.style.right;
    const prevOverflow = document.body.style.overflow;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = prevPosition || "";
      document.body.style.top = prevTop || "";
      document.body.style.left = prevLeft || "";
      document.body.style.right = prevRight || "";
      document.body.style.overflow = prevOverflow || "";
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // options
  const [locationOptions, setLocationOptions] = useState<string[]>([]);
  const [outletCategoryOptions, setOutletCategoryOptions] = useState<string[]>([]);
  const [pmiOptions, setPmiOptions] = useState<string[]>([]);
  const [outletNameOptions, setOutletNameOptions] = useState<string[]>([]);

  // selections
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedOutletCategories, setSelectedOutletCategories] = useState<string[]>([]);
  const [selectedPmi, setSelectedPmi] = useState<string[]>([]);
  const [selectedOutletNames, setSelectedOutletNames] = useState<string[]>([]);

  // loading flags
  const [loadingLocCat, setLoadingLocCat] = useState(false);
  const [loadingPmiOutlet, setLoadingPmiOutlet] = useState(false);

  // abort & bookkeeping
  const pmiOutletAbortRef = useRef<AbortController | null>(null);
  const onFetchedCalledRef = useRef(false);

  // cache PMI/Outlet by locations (+ category) (TTL)
  const cacheRef = useRef<Record<string, { pmi: string[]; outlets: string[]; expiry: number }>>({});
  const TTL_MS = 60 * 1000;

  // stable keys to cache/trigger
  const locationsKey = useMemo(
    () => (Array.isArray(selectedLocations) ? [...selectedLocations].sort().join("|") : ""),
    [selectedLocations]
  );
  const outletNamesKey = useMemo(
    () => (Array.isArray(selectedOutletNames) ? [...selectedOutletNames].sort().join("|") : ""),
    [selectedOutletNames]
  );
  const pmiKey = useMemo(
    () => (Array.isArray(selectedPmi) ? [...selectedPmi].sort().join("|") : ""),
    [selectedPmi]
  );
  const categoriesKey = useMemo(
    () => (Array.isArray(selectedOutletCategories) ? [...selectedOutletCategories].sort().join("|") : ""),
    [selectedOutletCategories]
  );

  const arraysEqual = (a: string[], b: string[]) =>
    a.length === b.length && a.every((v, i) => v === b[i]);

  /** Initial fetch for Locations & Outlet Category */
  useEffect(() => {
    if (!isOpen || !shouldFetch) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLocCat(true);
        const [locRes, catRes] = await Promise.allSettled([getalllocations(), getalloutlettype()]);

        if (cancelled) return;

        // locations
        if (locRes.status === "fulfilled") {
          const val: any = locRes.value;
          const arr =
            (Array.isArray(val?.data?.data) && val.data.data) ||
            (Array.isArray(val?.data) && val.data) ||
            (Array.isArray(val?.Data) && val.Data) ||
            (Array.isArray(val) && val) ||
            [];
          setLocationOptions(uniqueCI(arr.map((s: any) => cleanStr(s)))); // 🔹 NEW
        } else {
          setLocationOptions([]);
        }

        // outlet categories
        if (catRes.status === "fulfilled") {
          const val: any = catRes.value;
          const arr =
            (Array.isArray(val?.data?.data) && val.data.data) ||
            (Array.isArray(val?.data) && val.data) ||
            (Array.isArray(val?.Data) && val.Data) ||
            (Array.isArray(val) && val) ||
            [];
          setOutletCategoryOptions(uniqueCI(arr.map((s: any) => cleanStr(s)))); // 🔹 NEW
        } else {
          setOutletCategoryOptions([]);
        }

        if (!cancelled && !onFetchedCalledRef.current) {
          onFetchedCalledRef.current = true;
          onFetched?.();
        }
      } catch {
        if (!cancelled) {
          setLocationOptions([]);
          setOutletCategoryOptions([]);
        }
      } finally {
        if (!cancelled) setLoadingLocCat(false);
      }
    })();

    return () => {
      cancelled = true;
      onFetchedCalledRef.current = false;
    };
  }, [isOpen, shouldFetch, onFetched]);

  /**
   * Interdependent fetch for PMI & Outlet Names (with cache)
   * - Fetch PMI with: locations + selected outlet names + selected categories
   * - Fetch Outlet with: locations + selected PMI + selected categories (NOT outlet names)
   */
  useEffect(() => {
    if (!isOpen) return;

    if (pmiOutletAbortRef.current) {
      try {
        pmiOutletAbortRef.current.abort();
      } catch {}
    }
    const ctrl = new AbortController();
    pmiOutletAbortRef.current = ctrl;

    (async () => {
      try {
        setLoadingPmiOutlet(true);

        const now = Date.now();
        const hasOutletFilter = selectedOutletNames.length > 0;
        const hasPmiFilter = selectedPmi.length > 0;
        const hasCategoryFilter = selectedOutletCategories.length > 0;

        // Cache only when scoping by locations alone (no PMI/Outlet/Category)
        const cacheKey = `${locationsKey}::CAT=${categoriesKey}`;
        if (!hasOutletFilter && !hasPmiFilter && !hasCategoryFilter) {
          const cached = cacheRef.current[cacheKey];
          if (cached && cached.expiry > now) {
            setPmiOptions(cached.pmi);
            setOutletNameOptions(cached.outlets);
            // prune selections against cached options
            setSelectedPmi((prev) => {
              const pruned = prev.filter((p) => cached.pmi.includes(p));
              return arraysEqual(pruned, prev) ? prev : pruned;
            });
            setSelectedOutletNames((prev) => {
              const pruned = prev.filter((n) => cached.outlets.includes(n));
              return arraysEqual(pruned, prev) ? prev : pruned;
            });
            return;
          }
        }

        // ---- 1) Fetch PMI list ----
        const foForPmi = await getReportsFilterOptions_Ir(
          {
            locations: selectedLocations.length ? selectedLocations : null,
            outlet_name: selectedOutletNames.length ? selectedOutletNames : null,
            pmi_entity_code: null,
            outlet_category: selectedOutletCategories.length ? selectedOutletCategories : null,
          },
          token,
          ctrl.signal
        );
        if (ctrl.signal.aborted) return;

        const pmiList = Array.isArray(foForPmi?.pmiEntityCodes) ? foForPmi.pmiEntityCodes : [];
        setPmiOptions(pmiList);

        // 🔹 NEW: sync & normalize category options + prune invalid selections
        if (Array.isArray(foForPmi?.outletCategories)) {
          const normCats = uniqueCI(foForPmi.outletCategories.map(cleanStr));
          setOutletCategoryOptions(normCats);
          setSelectedOutletCategories((prev) => {
            const pruned = prev.filter((c) => normCats.includes(cleanStr(c)));
            return arraysEqual(pruned, prev) ? prev : pruned;
          });
        }

        // prune PMI selections
        setSelectedPmi((prev) => {
          const pruned = prev.filter((p) => pmiList.includes(p));
          return arraysEqual(pruned, prev) ? prev : pruned;
        });

        // ---- 2) Fetch Outlet list ----
        const foForOutlets = await getReportsFilterOptions_Ir(
          {
            locations: selectedLocations.length ? selectedLocations : null,
            outlet_name: null, // do not pass selected outlets
            pmi_entity_code: selectedPmi.length ? selectedPmi : null,
            outlet_category: selectedOutletCategories.length ? selectedOutletCategories : null,
          },
          token,
          ctrl.signal
        );
        if (ctrl.signal.aborted) return;

        const outletList = Array.isArray(foForOutlets?.outletNames) ? foForOutlets.outletNames : [];
        setOutletNameOptions(outletList);

        // 🔹 NEW: also normalize/sync categories here + prune
        if (Array.isArray(foForOutlets?.outletCategories)) {
          const normCats = uniqueCI(foForOutlets.outletCategories.map(cleanStr));
          setOutletCategoryOptions(normCats);
          setSelectedOutletCategories((prev) => {
            const pruned = prev.filter((c) => normCats.includes(cleanStr(c)));
            return arraysEqual(pruned, prev) ? prev : pruned;
          });
        }

        // prune Outlet selections
        setSelectedOutletNames((prev) => {
          const pruned = prev.filter((n) => outletList.includes(n));
          return arraysEqual(pruned, prev) ? prev : pruned;
        });

        // cache when neither side has active filters (and no category)
        if (!hasOutletFilter && !hasPmiFilter && !hasCategoryFilter) {
          cacheRef.current[cacheKey] = {
            pmi: pmiList,
            outlets: outletList,
            expiry: now + TTL_MS,
          };
        }
      } catch {
        if (!ctrl.signal.aborted) {
          setPmiOptions([]);
          setOutletNameOptions([]);
        }
      } finally {
        if (pmiOutletAbortRef.current === ctrl) pmiOutletAbortRef.current = null;
        setLoadingPmiOutlet(false);
      }
    })();

    return () => {
      if (pmiOutletAbortRef.current) {
        try {
          pmiOutletAbortRef.current.abort();
        } catch {}
        pmiOutletAbortRef.current = null;
      }
    };
  }, [isOpen, locationsKey, outletNamesKey, pmiKey, categoriesKey, token]);

  /** Rehydrate from initialFilters (once per open) */
  useEffect(() => {
    if (!initialFilters) return;
    setSelectedLocations(initialFilters.locations ?? []);
    setSelectedOutletCategories(initialFilters.outlet_category ?? []);
    setSelectedPmi(initialFilters.pmi_entity_code ?? []);
    setSelectedOutletNames(initialFilters.outlet_name ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters]);

  /** Actions */
  const handleClear = () => {
    setSelectedLocations([]);
    setSelectedOutletCategories([]);
    setSelectedPmi([]);
    setSelectedOutletNames([]);
    // clear cache on reset (prevents stale lists)
    cacheRef.current = {};
    onApplyFilters({
      locations: [],
      outlet_category: [],
      pmi_entity_code: [],
      outlet_name: [],
    });
    onCancel?.();
  };

  const handleApply = () => {
    onApplyFilters({
      locations: selectedLocations,
      outlet_category: selectedOutletCategories,
      pmi_entity_code: selectedPmi,
      outlet_name: selectedOutletNames,
    });
    onCancel?.();
  };

  return (
    <Box
      sx={{
        width: 420,
        maxHeight: "80vh",
        background: "#fff",
        borderRadius: 2,
        p: 0,
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2.5, borderBottom: "1px solid #eee" }}>
        <Typography sx={{ fontWeight: 600 }}>Apply Filter</Typography>
      </Box>

      {/* Body */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Locations</Typography>
          <SearchableMultiSelect
            label="Select Locations"
            options={locationOptions}
            value={selectedLocations}
            onChange={setSelectedLocations}
            loading={loadingLocCat}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Outlet Category</Typography>
          <SearchableMultiSelect
            label="Select Outlet Category"
            options={outletCategoryOptions}
            value={selectedOutletCategories}
            onChange={setSelectedOutletCategories}
            loading={loadingLocCat}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select PMI Entity Code</Typography>
          <SearchableMultiSelect
            label="Select PMI Entity Code"
            options={pmiOptions}
            value={selectedPmi}
            onChange={setSelectedPmi}
            loading={loadingPmiOutlet}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Outlet Names</Typography>
          <SearchableMultiSelect
            label="Select Outlet Names"
            options={outletNameOptions}
            value={selectedOutletNames}
            onChange={setSelectedOutletNames}
            loading={loadingPmiOutlet}
          />
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2.5,
          borderTop: "1px solid #eee",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.25,
        }}
      >
        <Button
          onClick={handleClear}
          sx={{
            border: "1px solid #E7E8E9",
            color: "#000",
            borderRadius: 2,
            height: 40,
          }}
        >
          CLEAR ALL
        </Button>
        <Button
          onClick={handleApply}
          sx={{
            border: "1px solid #C8172F",
            color: "#C8172F",
            borderRadius: 2,
            height: 40,
          }}
        >
          APPLY
        </Button>
      </Box>
    </Box>
  );
}
